import React, { useEffect, useState } from "react";
import styled from "styled-components";

import Button from "../button/Button";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../date-display/DateDisplay";
import SubcontractContractPdfPreview, {
  type SubcontractPdfPreviewState,
} from "../offer-wizard/SubcontractContractPdfPreview";
import type { ContractCardPhase } from "../../utils/offerStatus";
import { addConsumptionTax } from "../../utils/tax";

export type ContractCardSide = "sender" | "receiver";

/**
 * 契約書アイテム内に表示するオファー詳細サマリ。
 *
 * 「担当ロール」「スコープ」「成果物有無」はスレッド一覧 / 画面上部などに既に
 * 表示されているため本アイテムでは扱わない。ここにフィールドを追加する前に
 * 重複表示にならないかを必ず確認すること。
 */
export interface ContractCardOfferSummary {
  deadline: string | null;
  contract_amount: number | null;
}

interface ContractCardProps {
  phase: ContractCardPhase;
  side: ContractCardSide;
  /** 有効期限。`phase === "pre"` のときカウントダウン表示の対象になる。 */
  expiresAt?: string | null;
  /** 締結日時。`phase` が post / inspected のとき有効期限の代わりに表示する。 */
  concludedAt?: string | null;
  /** 送信遷移時の初期メッセージ本文（メッセージ最上部にプレーンテキストで表示）。 */
  initialMessage?: string | null;
  /**
   * 契約条件のサマリ。null/undefined ならサマリ部は出さない。
   *
   * 締結後に契約が更新されるとタイムラインに複数の版が並ぶため、**このカードが
   * 表す時点の値**を呼び出し側が解決して渡す（`DM.tsx` の `contractVersions`）。
   * スレッドの最新値をそのまま渡すと、初回の契約書が後から書き換わってしまう。
   */
  offerSummary?: ContractCardOfferSummary | null;
  /** 締結後に表示する契約書 PDF プレビュー状態（動的生成）。指定時は「詳細を確認」の代わりに表示する。 */
  contractPdfPreview?: SubcontractPdfPreviewState | null;
  /**
   * この契約書がまだ有効か。契約更新でタイムラインに複数の版が並ぶため、
   * `false`（= 更新されて無効になった版）は PDF に「無効」バッジを付けて沈める。
   */
  isContractActive?: boolean;
  onConfirm?: () => void;
  onEdit?: () => void;
  onReject?: () => void;
  onInvalidate?: () => void;
}

// 親メッセージ吹き出しに馴染ませるため、枠や背景は持たず内側コンテンツのみを構成する。
const Inner = styled.div<{ $faded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  ${({ $faded }) => $faded && "opacity: 0.6;"}
`;

const InitialMessageText = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  white-space: pre-wrap;
  word-break: break-word;
`;

const MetaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 8px;
  align-items: baseline;
`;

const MetaLabel = styled.div.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const MetaValue = styled.div.attrs({ className: "bodytext-13" })``;

const Divider = styled.div`
  height: 1px;
  background: var(--soremo-border);
`;

// 締結済み契約書 PDF プレビューのラッパー（通常メッセージのファイル添付と同じ見た目）。
const ContractFilePreview = styled.div`
  width: 100%;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const EMPTY_DASH = "—";

/**
 * 有効期限までの残り時間を「あと D日 H時間 M分 S秒」形式で 1 秒ごとに更新する。
 * `target` が `null` / 不正値 / 過去なら `null` を返し、UI 側でフォールバック表示する。
 */
const useCountdown = (target: string | null | undefined): string | null => {
  const [text, setText] = useState<string | null>(() =>
    computeCountdownText(target),
  );

  useEffect(() => {
    setText(computeCountdownText(target));
    if (!target) return undefined;
    const interval = window.setInterval(() => {
      setText(computeCountdownText(target));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [target]);

  return text;
};

function computeCountdownText(
  target: string | null | undefined,
): string | null {
  if (!target) return null;
  const t = new Date(target).getTime();
  if (Number.isNaN(t)) return null;
  const diff = t - Date.now();
  if (diff <= 0) return "期限切れ";
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
  const seconds = Math.floor((diff % (60 * 1000)) / 1000);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}日`);
  if (days > 0 || hours > 0) parts.push(`${hours}時間`);
  if (days > 0 || hours > 0 || minutes > 0) parts.push(`${minutes}分`);
  parts.push(`${seconds}秒`);
  return `あと ${parts.join(" ")}`;
}

const ContractCard: React.FC<ContractCardProps> = ({
  phase,
  side,
  expiresAt,
  concludedAt,
  initialMessage,
  offerSummary,
  contractPdfPreview,
  isContractActive = true,
  onConfirm,
  onEdit,
  onReject,
  onInvalidate,
}) => {
  // `pre` / `post` / `inspected` / `invalid` で表示・操作が変化する。フラグ展開は
  // 「読み手が条件式から意図を即座に読み取れる」最小限にとどめる。
  const isInvalid = phase === "invalid";
  const isPre = phase === "pre";

  // `pre` のときだけ有効期限カウントダウンを動かす（他フェーズでは setInterval を回さない）。
  const countdownText = useCountdown(isPre ? expiresAt : undefined);

  // 締結済み契約書 PDF があれば「詳細を確認」ボタンの代わりに PDF プレビューを表示する。
  // （送信者・受信者とも、クリックでプレビューが開く通常のファイル添付と同じ挙動）
  const hasContractPdf = !!contractPdfPreview;

  // 表示・アクションの出し分け（phase × side の単純マトリクス）。
  const showConfirm = side === "receiver" && !isInvalid && !hasContractPdf;
  const showEdit = isPre && side === "sender";
  const showReject = isPre && side === "receiver";
  const showInvalidate = isPre && side === "sender";
  const hasActions = showReject || showInvalidate || showConfirm || showEdit;

  const hasInitialMessage =
    !!initialMessage && initialMessage.trim().length > 0;
  const hasMeta = !!offerSummary || (isPre ? !!expiresAt : !!concludedAt);

  return (
    <Inner
      $faded={isInvalid}
      data-testid="contract-card"
      data-phase={phase}
      data-side={side}
    >
      {hasInitialMessage && (
        <InitialMessageText>{initialMessage}</InitialMessageText>
      )}

      {hasInitialMessage && hasMeta && <Divider />}

      {hasMeta && (
        <MetaList>
          {offerSummary && (
            <>
              <MetaRow>
                <MetaLabel>納期</MetaLabel>
                <MetaValue>
                  {offerSummary.deadline ? (
                    <DateDisplay
                      date={offerSummary.deadline}
                      type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
                    />
                  ) : (
                    EMPTY_DASH
                  )}
                </MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>対価</MetaLabel>
                <MetaValue>
                  {offerSummary.contract_amount === null ||
                  offerSummary.contract_amount === undefined
                    ? EMPTY_DASH
                    : `${addConsumptionTax(offerSummary.contract_amount).toLocaleString("ja-JP")}円（税込）`}
                </MetaValue>
              </MetaRow>
            </>
          )}
          {isPre ? (
            <MetaRow>
              <MetaLabel>有効期限</MetaLabel>
              <MetaValue>{countdownText ?? EMPTY_DASH}</MetaValue>
            </MetaRow>
          ) : (
            concludedAt && (
              <MetaRow>
                <MetaLabel>締結日時</MetaLabel>
                <MetaValue>
                  <DateDisplay
                    date={concludedAt}
                    type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
                  />
                </MetaValue>
              </MetaRow>
            )
          )}
        </MetaList>
      )}

      {hasContractPdf && (
        <>
          {(hasInitialMessage || hasMeta) && <Divider />}
          <ContractFilePreview>
            <SubcontractContractPdfPreview
              preview={contractPdfPreview as SubcontractPdfPreviewState}
              variant="message"
              statusLabel={isContractActive ? "締結済み" : "無効"}
              isSuperseded={!isContractActive}
            />
          </ContractFilePreview>
        </>
      )}

      {hasActions && (
        <>
          {(hasInitialMessage || hasMeta || hasContractPdf) && <Divider />}
          <ActionRow>
            {showReject && (
              <Button
                size="small"
                theme="tertiary"
                variant="outlined"
                width="fit"
                onClick={onReject}
              >
                今回は辞退
              </Button>
            )}
            {showInvalidate && (
              <Button
                size="small"
                theme="tertiary"
                variant="outlined"
                width="fit"
                onClick={onInvalidate}
              >
                キャンセル
              </Button>
            )}
            <ActionRight>
              {showConfirm && (
                <Button
                  size="small"
                  theme={isPre ? "primary" : "tertiary"}
                  variant={isPre ? "filled" : "outlined"}
                  width="fit"
                  rightIcon="chevron_right"
                  onClick={onConfirm}
                >
                  詳細を確認
                </Button>
              )}
              {showEdit && (
                <Button
                  size="small"
                  theme="primary"
                  variant="filled"
                  width="fit"
                  rightIcon="chevron_right"
                  onClick={onEdit}
                >
                  内容の編集
                </Button>
              )}
            </ActionRight>
          </ActionRow>
        </>
      )}
    </Inner>
  );
};

export default ContractCard;
